import { Component, Inject, Input, LOCALE_ID, OnInit } from '@angular/core';
import { OrderDetailModel } from '../../../state/model/order-detail.model';
import { QualityDocumentService } from '../../../service/document-quality.service';
import { Router } from '@angular/router';
import { DROPDOWN_BUTTON_ITEM_TYPE, DropdownButtonItem } from '@cv-invision/cv.component-library.ux';
import { DOCUMENT_ATTACHMENT_CATEGORIES, ORDER_STATE } from '../../../model/quality.constant';
import { Observable, Subject, takeUntil } from 'rxjs';
import { formatDate } from '@angular/common';
import { PdfAttachmentsStateModel } from '../../../pdf-review/state/model/pdf-attachments-state.model';
import { Store } from '@ngxs/store';
import { OverviewSubHeaderService } from '../service/overview-sub-header.service';
import { BroadcastService, ORDER_EVENT_TYPE } from '@cv-invision/core';

@Component({
	selector: 'cv-download-buttons',
	templateUrl: `./download-buttons.component.html`,
	styleUrls: ['./download-buttons.component.scss'],
})
export class DownloadButtonsComponent implements OnInit {
	@Input() orderInboxModel: OrderDetailModel;
	private atComponentDestroy = new Subject();
	pdfAttachments$: Observable<PdfAttachmentsStateModel>;

	isAvailable: boolean;
	pdfDropdownItems: any[];
	loading: boolean;
	src: any;
	qrrReportPath: string;


	constructor(
		private qualityDocumentService: QualityDocumentService,
		private router: Router,
		private store: Store,
		private overviewSubheaderService: OverviewSubHeaderService,
		private broadcastService: BroadcastService,
		@Inject(LOCALE_ID) private locale: string,
	) {
		this.pdfAttachments$ = this.store.select(s => s.quality.pdfAttachments);
		this.pdfDropdownItems = [];
		this.isAvailable = true;
		this.loading = true;
	}

	ngOnInit(): void {
		this.checkForPDFFile();
		this.buildPDFDropdownItems();

		this.overviewSubheaderService
			.subheaderEvents
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((event: any) => {
				if (event.name === ORDER_EVENT_TYPE.QRR_REPORT_GENERATED) {
					this.buildPDFDropdownItems();
				}
			});

		this.broadcastService.on('loadPDFFail', result => {
			if (result === 'Not Available') {
				this.isAvailable = false;
			}
		});

		this.broadcastService.on('loadPDFSuccess', () => {
			setTimeout(() => {
				this.loading = false;
				if (
					this.orderInboxModel &&
					this.qualityDocumentService.currentPDFFileId() === this.orderInboxModel.id
				) {
					this.src = this.qualityDocumentService.loadCurrentPDF();
				}
			});
		});
	}


	/**
	 * Build the PDF dropdown items based on the current order pdf attachments.
	 */
	private buildPDFDropdownItems(): void {
		const { stateEnum } = this.orderInboxModel;

		if (
			stateEnum === ORDER_STATE.APPROVED &&
			this.orderInboxModel.schema.indexOf('InspectionReport') === -1
		) {
			this.qualityDocumentService
				.getOrderDocumentAttachment(
					this.orderInboxModel.id,
					DOCUMENT_ATTACHMENT_CATEGORIES.FinalReport
				)
				.pipe(takeUntil(this.atComponentDestroy))
				.subscribe(result => {
					this.addQRRPDFDropdownItems(result);
				});
		} else {
			this.pdfDropdownItems = [
				{
					type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
					label: 'View current PDF version',
					icon: 'fa-file-pdf'
				}
			];

			this.addCommonPDFDropdownItems();
		}
	}

	/**
	 * Add QRR Report items to PDF Dropdown list
	 */
	private addQRRPDFDropdownItems(result: any): void {
		this.pdfDropdownItems = [
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: 'View current PDF version',
				icon: 'fa-file-pdf'
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			}
		];

		if (result.length) {
			this.qrrReportPath = result[0].path;

			this.pdfDropdownItems.push({
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: 'Open QRR report',
				icon: 'fa-external-link-alt'
			});
		} else {
			this.pdfDropdownItems.push({
				type: DROPDOWN_BUTTON_ITEM_TYPE.LABEL,
				label: 'Loading QRR report...'
			});

			// Regenerate QRR Report
			this.qualityDocumentService
				.regenerateQRRReport(this.orderInboxModel.id)
				.pipe(takeUntil(this.atComponentDestroy))
				.subscribe(data => {
					// Wait for signal alert
				});
		}

		this.addCommonPDFDropdownItems();
	}

	/**
	 * Add common items to PDF Dropdown list
	 */
	private addCommonPDFDropdownItems(): void {
		this.pdfDropdownItems.push({
			type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
		});

		this.pdfDropdownItems.push({
			type: DROPDOWN_BUTTON_ITEM_TYPE.LABEL,
			label: 'Compare PDF documents:'
		});

		this.pdfAttachments$
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((pdfAttachments: PdfAttachmentsStateModel) => {
				if (pdfAttachments && pdfAttachments.attachments && pdfAttachments.attachments.data) {
					const pdf = pdfAttachments.attachments.data.map(document => {
						return {
							type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
							label: formatDate(
								document.documentCreatedDate,
								'MMMM dd, yyyy - hh:mm aa',
								this.locale
							),
							icon: 'fa-file-pdf',
							data: document
						};
					});
					const filterOption = this.pdfDropdownItems.filter(
						i => !pdf.some(p => i.data && p.data.id === i.data.id)
					);
					this.pdfDropdownItems = [].concat(filterOption, pdf);
				}
			});
	}


	/**
 * Check for the PDF file load.
 */
	private checkForPDFFile(): void {
		if (!this.qualityDocumentService.isLoadingPDF()) {
			if (this.qualityDocumentService.currentPDFFileId() === this.orderInboxModel.id) {
				this.loading = false;
				this.src = this.qualityDocumentService.loadCurrentPDF();
			}
		}
	}



	/**
 * Download PDF file..
 */
	onDownloadPDF(src: any = null, filename?: string): void {
		const element = document.createElement('a');
		if (!src) {
			src = this.src;
			filename = this.orderInboxModel.description;
		}
		element.href = URL.createObjectURL(src);
		element.download = `${filename}.pdf`;
		document.body.appendChild(element);
		element.click();
	}

	/**
	 * View PDF version file.
	 */
	onViewPDFversion($event: { index: number; item: DropdownButtonItem }): void {
		if ($event.index === 0) {
			this.router.navigate([`intelligence/quality/pdfreview/${this.orderInboxModel.id}`]);
		} else if ($event.item.label === 'Open QRR report') {
			this.loading = true;
			this.qualityDocumentService.downloadFile(this.qrrReportPath).subscribe(
				result => {
					this.loading = false;
					if (result.blob) {
						this.onDownloadPDF(result.blob, 'QRR-' + this.orderInboxModel.id);
					}
				},
				error => {
					this.loading = false;
				}
			);
		} else if ($event.item.data && $event.item.data.path) {
			this.loading = true;
			this.qualityDocumentService.downloadFile($event.item.data.path).subscribe(
				result => {
					this.loading = false;
					if (result.blob) {
						this.onDownloadPDF(result.blob, $event?.item?.label);
					}
				},
				error => {
					this.loading = false;
				}
			);
		}
	}
}
